import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo, siteInfo } from '@/mocks/siteData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    setFormStatus('loading');
    setFormError('');
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9q9cpee3oq4jqumtih0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(parsed?.meta?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <main>
        <Navbar />

        {/* Breadcrumb */}
        <section className="pt-24 pb-6 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]} />
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-10 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-8">{aboutInfo.brand.title}</h1>
            <div className="space-y-5">
              {aboutInfo.brand.paragraphs.map((p, i) => (
                <p key={i} className="text-sm text-foreground-600 leading-relaxed">{p}</p>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-12 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8 text-center">{aboutInfo.reputation.title}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {aboutInfo.reputation.items.map((item) => (
                <div key={item.label} className="text-center p-5 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">{item.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-2">{aboutInfo.contact.title}</h2>
              <p className="text-sm text-foreground-500">{aboutInfo.contact.description}</p>
            </div>

            {/* Contact Info */}
            <div className="flex flex-col sm:flex-row gap-6 mb-10 justify-center">
              <div className="flex items-center gap-3 text-sm text-foreground-600">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-mail-line"></i>
                </div>
                <span>{aboutInfo.contact.email}</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-foreground-600">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                  <i className="ri-map-pin-line"></i>
                </div>
                <span>{aboutInfo.contact.address}</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-foreground-600">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-time-line"></i>
                </div>
                <span>{aboutInfo.contact.hours}</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-xs text-foreground-600 mb-1.5">姓名</label>
                  <input type="text" id="name" name="name" required className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors" placeholder="您的姓名" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs text-foreground-600 mb-1.5">邮箱</label>
                  <input type="email" id="email" name="email" required className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors" placeholder="your@email.com" />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-xs text-foreground-600 mb-1.5">主题</label>
                <input type="text" id="subject" name="subject" required className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors" placeholder="请输入主题" />
              </div>
              <div>
                <label htmlFor="message" className="block text-xs text-foreground-600 mb-1.5">留言</label>
                <textarea id="message" name="message" required maxLength={500} rows={4} className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none" placeholder="请输入您的留言（最多500字）"></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500字</p>
              </div>
              <div className="anti-spam-field">
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>
              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {formStatus === 'loading' ? '提交中...' : '发送留言'}
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-secondary-600">留言已成功发送！我们会尽快与您联系。</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-500">{formError}</p>
              )}
            </form>
          </div>
        </section>

        {/* SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": `关于${siteInfo.name}`,
            "description": aboutInfo.brand.paragraphs.join(' '),
            "url": "https://position-guide.com/about",
            "about": {
              "@type": "Organization",
              "name": siteInfo.name,
              "url": "https://position-guide.com/",
              "foundingDate": siteInfo.foundedYear,
              "contactPoint": {
                "@type": "ContactPoint",
                "email": aboutInfo.contact.email,
                "contactType": "customer service"
              }
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://position-guide.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "关于我们",
                "item": "https://position-guide.com/about"
              }
            ]
          })}
        </script>

        <Footer />
      </main>
    </>
  );
}